import { FileText, BookOpen, Code2, LifeBuoy, Rss, ArrowUpRight, Boxes, Gauge, Users, Map, Github } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SupportModal } from "@/components/SupportModal";

const DOCS = [
  { icon: BookOpen, t: "Getting Started", d: "Install the SDK, generate an API key and spin up your first Golang game service in minutes." },
  { icon: Code2, t: "API Reference", d: "Full reference for the Notebook, AI Workflow and AI Automation endpoints, with request/response examples." },
  { icon: FileText, t: "Architecture Guides", d: "Battle-tested patterns for matchmaking, turn-based state, leaderboards, inventories and caching." },
  { icon: Boxes, t: "Deploy & Operate", d: "Dockerfiles, Kubernetes manifests, autoscaling, observability and zero-downtime rollouts on Linux." },
  { icon: Gauge, t: "Performance Tuning", d: "MySQL/PostgreSQL indexing, Redis/Memcached strategies and profiling for sub-5ms reads." },
  { icon: Code2, t: "Languages & SDKs", d: "Work across Golang, PHP, C/C++, Python (Pandas), Panda3D and Matlab from one engine." },
];

const RESOURCES = [
  { icon: Rss, t: "Engineering Blog", d: "Deep-dives, post-mortems and patterns from real game backends." },
  { icon: Users, t: "Community", d: "Join other builders to share architectures and get unblocked fast." },
  { icon: Map, t: "Roadmap", d: "See what's shipping next and vote on what matters to you." },
  { icon: Github, t: "Examples & Templates", d: "Starter repos for turn-based, real-time and analytics stacks." },
];

export const DocsResources = () => {
  const navigate = useNavigate();
  const [support, setSupport] = useState(false);
  return (
    <section id="docs" className="py-24 px-4 sm:px-6 lg:px-8 bg-background">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <span className="text-xs font-mono uppercase tracking-widest text-emerald-400">Docs &amp; Resources</span>
            <h2 className="mt-3 font-display text-3xl lg:text-4xl font-bold tracking-tight text-slate-100">Everything you need to build fast</h2>
            <p className="mt-4 text-slate-400 leading-relaxed">Clear, practical documentation and a growing library of resources — from first install to production tuning.</p>
          </div>
          <button onClick={() => setSupport(true)} data-testid="docs-support-btn"
            className="inline-flex items-center gap-2 text-sm text-emerald-400 hover:text-emerald-300 transition-colors font-medium">
            <LifeBuoy className="w-4 h-4" /> 24/7 Technical Support
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {DOCS.map((d, i) => (
            <button key={d.t} data-testid={`docs-card-${i}`} onClick={() => setSupport(true)}
              className="group text-left glass rounded-xl border border-slate-800 p-6 hover:border-emerald-500/50 hover:-translate-y-1 transition-[transform,border-color] duration-300">
              <d.icon className="w-6 h-6 text-emerald-400 mb-4" />
              <h3 className="font-display font-semibold text-slate-100 flex items-center gap-1">
                {d.t} <ArrowUpRight className="w-4 h-4 text-slate-600 group-hover:text-emerald-400 transition-colors" />
              </h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{d.d}</p>
            </button>
          ))}
        </div>

        <h3 id="resources" className="mt-16 mb-5 text-sm font-mono uppercase tracking-widest text-slate-500">Resources</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {RESOURCES.map((r, i) => (
            <button key={r.t} data-testid={`resource-card-${i}`} onClick={() => setSupport(true)}
              className="group text-left glass rounded-xl border border-slate-800 p-6 hover:border-emerald-500/50 transition-colors">
              <r.icon className="w-6 h-6 text-emerald-400 mb-4" />
              <h3 className="font-display font-semibold text-slate-100 text-sm">{r.t}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{r.d}</p>
            </button>
          ))}
        </div>

        <div className="mt-8">
          <button onClick={() => navigate("/terms")} data-testid="docs-terms-link"
            className="text-sm text-slate-500 hover:text-emerald-400 transition-colors font-mono">→ Terms &amp; Policy</button>
        </div>
      </div>
      <SupportModal open={support} onClose={() => setSupport(false)} />
    </section>
  );
};
